import axios from "axios";
import { baseUrl } from ".";
import { ResultMessageProps } from "./user";

export interface PropertiesSearch {
  page: Number;
  limit: Number;
  propertyType?: String;
  minPrice?: Number;
  maxPrice?: Number;
  minGrossYield?: Number;
  maxGrossYield?: Number;
  searchKeyword?: String;
  sortDate?: string;
  region?: string;
}

export interface Filter {
  maxGrossYield: number;
  maxPrice: number;
  minGrossYield: number;
  minPrice: number;
  minBedroom: number;
  maxBedroom: number;
  propertyType: string[];
  strategy: string[];
  regions: string[];
  towns: string[];
}
export interface Pagination {
  total: number;
  next: number;
  prev: number;
}

export interface Theme {
  color1: String;
  color2: String;
  logo: String;
  paymentAccept: Boolean;
}

export interface PropertiesExtraResult {
  results: Property[];
  filter: Filter;
  message: String;
  pagination: Pagination;
  theme: Theme;
}

export interface PropertyExtraResult {
  results: Property;
  message: String;
  theme: Theme;
}

export interface Property {
  results: any;
  region: string;
  someField: any;
  propertykeyFeatures?: string[] | null;
  strategy?: string | null;
  currentRent?: number | null;
  images?: ImagesEntity[] | null;
  sizing?: string[] | null;
  wishlist: boolean;
  rentComparable?: string[] | null;
  saleComparable?: string[] | null;
  marketComparable?: null[] | null;
  streetComparable?: null[] | null;
  benefits?: string[] | null;
  refurbishments?: string[] | null;
  nearestStation?: NearestStationEntity[] | null;
  nearestSchool?: NearestSchoolEntity[] | null;
  createdAt: string;
  updatedAt: string;
  listingViewDate?: string;
  _id: string;
  propertyId: string;
  connectedTo: string;
  propertyDescription: string;
  propertyOf: string;
  propertyAddress: PropertyAddress;
  website: string;
  longitude?: null;
  latitude?: null;
  postcode: string;
  lhaRent: number;
  price: number;
  bedrooms: number;
  buildingUnits?: number;
  isListingLive: boolean;
  houseType: string;
  houseSubtype: string;
  localcontact: string;
  houseNumber: string;
  averageRent: number;
  lhaGrossYield?: null;
  lhaNetYield?: null;
  rentGrossYield: number;
  rentNetYield?: null;
  averagePriceValution?: null;
  rentPropertyMacthed?: null;
  fullAddress: string;
  estateAgentEmail: string;
  estateAgentPhone: string;
  estateAgentName: string;
  brandName: string;
  branchName: string;
  discountToMarket: number;
  discountLabel?: string;
  returnToMarket: number;
  listedDate: string;
  tenureType: string;
  username: string;
  userHandle: string;
  agentAddress: string;
  agentPhone: string;
  refurbishmentCost: number;
  epc: string;
  floorsqft: string;
  floodRisk: string;
  offerPrice: number;
  soldStc: boolean;
  reserved: boolean;
  publishStatus?: "available" | "withdrawn" | "paused" | "reserved" | "held" | "sold" | "inLegals";
  reservationPrice: number;
  propertyFrom: string;
  estimatedValue: number;
  legal: number;
  investment: number;
  occupancy: string;
  depositeProperty: number;
  management: number;
  brokerage: number;
  opex: number;
  mortgage: number;
  mortgageLabel: string;
  netCashflow: number;
  setupFeeValuation: number;
  annualCashflow: number;
  stampDuty: number;
  asteroomUrl?: string;
  __v: number;
}
export interface ImagesEntity {
  url: string;
  caption?: null;
  resizedImageUrls: ResizedImageUrls;
}
export interface ResizedImageUrls {
  size135x100: string;
  size476x317: string;
  size656x437: string;
}
export interface NearestStationEntity {
  station: string;
  distance: string;
}
export interface NearestSchoolEntity {
  school: string;
  distance: string;
}
export interface PropertyAddress {
  displayAddress: string;
  countryCode: string;
  deliveryPointId?: null;
  ukCountry: string;
  outcode: string;
  incode: string;
}

interface EnquiryData {
  name: string;
  phoneNumber: string;
  email: string;
  productId: string;
  userId?: string;
  websiteName?: string;
  websiteURL?: string;
  // NEW FIELDS
  serviceOwnService?: boolean;
  calendarLinkUsed?: string | null;
  calendarCompleted?: boolean;
  appointmentDateTime?: Date;
  updateExisting?: boolean; // Flag to indicate this is an update
  // ADDITIONAL FIELDS FOR FORM MODAL
  formModalUsed?: boolean;
  formCompleted?: boolean;
  formData?: string;
}

interface EnquiryFormData {
  enquiryId: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  productId: string;
  createdAt: string;
  formModalUsed?: boolean;
  formCompleted?: boolean;
}

// Function to fetch enquiry data by ID for form prepopulation
export const fetchEnquiryData = async (enquiryId: string, token?: string): Promise<EnquiryFormData | null> => {
  try {
    if (!token || !enquiryId) {
      console.log('No token or enquiry ID available');
      return null;
    }

    // Strip the B9I9N9456 suffix from the token if present
    const jwt = token.replace("B9I9N9456", "");

    const response = await fetch(`${baseUrl}/properties/enquiries/${enquiryId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('Enquiry not found');
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    console.log('Enquiry data fetched successfully:', {
      enquiryId: result.data.enquiryId,
      hasName: !!result.data.name,
      hasEmail: !!result.data.email
    });

    return result.data;
  } catch (error) {
    console.error('Error fetching enquiry data:', error);
    return null;
  }
};

export const sendEnquiry = async (data: EnquiryData) => {
  try {
    const response = await fetch(`${baseUrl}/properties/enquiries`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${window.__TENANT__.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: data.name,
        phoneNumber: data.phoneNumber,
        email: data.email,
        productId: data.productId,
        userId: data.userId,
        websiteName: data.websiteName,
        websiteURL: data.websiteURL,
        // NEW FIELDS
        serviceOwnService: data.serviceOwnService || false,
        calendarLinkUsed: data.calendarLinkUsed || null,
        calendarCompleted: data.calendarCompleted || false,
        appointmentDateTime: data.appointmentDateTime || null,
        updateExisting: data.updateExisting || false,
        formModalUsed: data.formModalUsed || false,
        formCompleted: data.formCompleted || false,
        formData: data.formData || null,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Return both the result and the enquiryId for form prepopulation
    return {
      ...result,
      enquiryId: result.data?.enquiryId || result.enquiryId // Handle both response formats
    };
  } catch (error) {
    console.error('Error submitting enquiry:', error);
    throw error;
  }
};

export const getProperties = async (
  data: PropertiesSearch
): Promise<PropertiesExtraResult> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/properties`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    window.location.reload();
    return undefined as any;
  }
};


export const getFeaturedProperties =
  async (): Promise<PropertiesExtraResult> => {
    try {
      const response = await axios.get(
        `${baseUrl}/whitelabel/getFeaturedProperties`,
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
            Authorization: `Bearer ${window.__TENANT__.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.log(error);
      window.location.reload();
      return undefined as any;
    }
  };

export const getProperty = async (id: string): Promise<PropertyExtraResult> => {
  try {
    const response = await axios.get(
      `${baseUrl}/whitelabel/singleProperty/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return undefined as any;
  }
};

export const checkout = async ({ id, url, token }: any): Promise<any> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/checkout`,
      { _id: id, url: url },
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${token.replace("B9I9N9456", "")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return undefined as any;
  }
};

interface OrderParamsProps {
  connectedTo: string;
  paymentStatus: string;
  token: string;
}

export const orderProperty = async (
  data: OrderParamsProps
): Promise<ResultMessageProps> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/reserve`,
      {
        connectedTo: data.connectedTo,
        paymentStatus: data.paymentStatus,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token.replace("B9I9N9456", "")}`,
        },
      }
    );
    return { status: response.status, message: response.data.message };
  } catch (error: any) {
    console.log(error);
    return {
      status: error.response.status,
      message: error.response.data.message,
    };
  }
};

export interface ContactFormProps {
  email: string;
  firstname: string;
  lastname: string;
  mobilephone: string;
  message: string;
}

export const contactFormContact = async (
  data: ContactFormProps
): Promise<ResultMessageProps> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/getintouch`,
      {
        email: data.email,
        firstname: data.firstname,
        lastname: data.lastname,
        mobilephone: data.mobilephone,
        message: data.message,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return { status: response.status, message: response.data.message };
  } catch (error: any) {
    console.log(error);
    return {
      status: error.response.status,
      message: error.response.data.message,
    };
  }
};

interface MakeAnOfferProps {
  connectedTo: string;
  offer: string;
  propertyLink: string;
  token: string;
}

export const postMakeAnOffer = async ({
  connectedTo,
  offer,
  propertyLink,
  token,
}: MakeAnOfferProps): Promise<ResultMessageProps> => {
  try {
    // console.log(connectedTo, offer, propertyLink, token);
    const response = await axios.post(
      `${baseUrl}/whitelabel/makeoffer`,
      {
        connectedTo,
        offer,
        propertyLink,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.replace("B9I9N9456", "")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return undefined as any;
  }
};

export const numberFormat = (value: number) => {
  let internationalNumberFormat = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return internationalNumberFormat.format(value);
};

export interface SandboxPropertyResult extends PropertyExtraResult {
  sandboxMode: boolean;
  whitelabelTheme?: any;
}

const sandboxApiBase = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/v1`
  : baseUrl;

export const getSandboxProperty = async (token: string): Promise<SandboxPropertyResult> => {
  const response = await axios.get(
    `${sandboxApiBase}/whitelabel/sandbox/property/${token}`,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export interface NewBuildUnit {
  unit?: string;
  beds?: number;
  price?: number;
  status?: 'available' | 'sold' | 'held' | 'unknown';
  available?: boolean;
  [key: string]: any;
}

export interface NewBuildAvailabilitySheet {
  id: number;
  name: string;
  notes?: string;
  url: string;
}

export interface NewBuildFaq {
  question: string;
  answer: string;
}

export interface NewBuildPropertyItem {
  _id: string;
  title?: string;
  developer?: string;
  development?: string;
  address?: string;
  location?: string;
  hook?: string;
  headline?: string;
  highlights?: string;
  selling_points?: string[];
  faqs?: NewBuildFaq[];
  price_from?: string;
  price_to?: string;
  no_of_units?: number;
  no_of_levels?: number;
  image?: string;
  images?: { url: string; [key: string]: any }[];
  units?: NewBuildUnit[];
  availability_sheets?: NewBuildAvailabilitySheet[];
  published: boolean;
  grossYield?: number;
  purchasePrice?: number;
  averageRent?: number;
  postcode?: string;
  region?: string;
  town?: string;
  propertyType?: string;
  tenureType?: string;
  hubDirectId?: number;
  last_synced_at?: string;
  createdAt?: string;
  listingViewDate?: string;
  listingOrder?: number;
  deposite?: number;
  lettingAgentFees?: number;
  latitude?: number;
  longitude?: number;
  schools?: any[];
  stations?: any[];
  onStreetCompare?: any[];
  onDistrictCompare?: any[];
  onForSaleCompare?: any[];
  onForRentCompare?: any[];
}

export interface NewBuildListResult {
  results: NewBuildPropertyItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
  message: string;
}

export interface NewBuildSingleResult {
  results: NewBuildPropertyItem | null;
  message?: string;
}

export const getNewBuildProperties = async (data: {
  page: number;
  limit: number;
}): Promise<NewBuildListResult> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/new-build-properties`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return { results: [], pagination: { total: 0, page: 1, limit: 30 }, message: 'error' };
  }
};

export const getNewBuildProperty = async (id: string): Promise<NewBuildSingleResult> => {
  try {
    const response = await axios.get(
      `${baseUrl}/whitelabel/singleNewBuildProperty/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return { results: null, message: 'error' };
  }
};
export const approveSandboxPreview = async (token: string): Promise<{ previewAccepted: string; previewIP: string }> => {
  const response = await axios.post(
    `${sandboxApiBase}/whitelabel/sandbox/approve`,
    { token },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};
